/**
 * @file: light.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { Colors, StatusColors } from '@euler/components/Colors';
import Color from 'color';
import { DefaultTheme } from 'styled-components';

const theme: DefaultTheme = {
  colors: {
    primary: '#009FD4',
    secondary: '#7B4BF6',
    status: {
      info: StatusColors.Info,
      warn: StatusColors.Warn,
      danger: StatusColors.Danger,
      success: StatusColors.Success,
      notice: StatusColors.Notice,
    },
    severityLevel: {
      normal: StatusColors.Success,
      defective: StatusColors.Notice,
      warning: StatusColors.Warn,
      urgent: StatusColors.Danger,
    },
    defectiveLevel: {
      normal: StatusColors.Success,
      defective: StatusColors.Notice,
      warning: StatusColors.Warn,
      urgent: StatusColors.Danger,
    },
  },
  page: {
    background: '#eee',
    card: {
      backgroundColor: '#eee',
    },
    header: {
      titleColor: '#000',
      subTitleColor: '#444',
    },
  },
  label: {
    color: '#000',
    destructiveColor: StatusColors.Danger,
  },
  section: {
    background: Colors.White,
  },
  link: '#2669db',
  actionButton: {
    textColorLight: '#fff',
    textColorDark: '#333',
    defaultBgColor: '#ccc',
    defaultTextColor: '#333',
    whiteBgColor: '#fff',
    whiteTextColor: '#fff',
  },
  form: {
    label: { color: '#444' },
    input: {
      borderColor: '#888',
      borderColorFocused: '#2669db',
      borderColorError: StatusColors.Danger,
      backgroundColor: '#fff',
      backgroundColorFocused: '#fff',
      backgroundColorError: '#fff1f0',
      placeholderColor: '#ccc',
      textColor: '#000',
      textColorFocused: '#000',
      textColorError: StatusColors.Danger,
    },
  },
  components: {
    placeholder: {
      background: Colors.Gray10,
    },
    card: {
      background: Colors.White,
      titleColor: Colors.Black,
      headerRightColor: Colors.Gray4,
      shadowColor: '#cccccc',
      borderColor: '#eee',
    },
    emptyView: {
      iconColor: Colors.Gray3,
      textColor: Colors.Gray3,
      retryButtonBgColor: Colors.Gray3,
    },
    scrollView: {
      activityIndicator: {
        color: Colors.Gray3,
      },
    },
    button: {
      disabledBgColor: Colors.Gray4,
    },
    segment: {
      textColor: Colors.Gray2,
      background: '#e8e8e8',
      activeBgColor: '#ffffff',
    },
    switch: {
      border: {
        inactive: '#BFBFBF',
        active: '#207FE7',
      },
      thumb: {
        inactive: '#ffffff',
        active: '#ffffff',
      },
      track: {
        inactive: {
          startColor: '#f5f5f5',
          endColor: '#f5f5f5',
        },
        active: {
          startColor: '#207FE7',
          endColor: '#207FE7',
        },
      },
    },
    table: {
      section: {
        titleColor: Colors.Black,
        tailColor: Colors.Gray3,
      },
      item: {
        titleColor: Colors.Black,
        subTitleColor: Colors.Gray3,
        noteColor: Colors.Gray2,
        detailColor: Colors.Gray4,
        detailDisclosureIconColor: Colors.Gray4,
        detailInfoIconColor: StatusColors.Info,
        backgroundColor: Colors.White,
      },
      index: {
        color: '#666666',
        activeBackgroundColor: '#c4c4c4',
      },
      borderColor: Colors.Gray8,
    },
    snackbar: {
      backgroundColor: '#fff',
      textColor: '#333',
      closeIconColor: '#666',
    },
  },
  tv: {
    focusShadeColor: 'rgba(162, 135, 244, 0.25)',
  },
  keyboard: {
    bgColor: '#D5D8DD',
    keyBgColor: '#FFFFFF',
    funcKeyBgColor: '#B3B8C1',
    keyColor: '#000000',
    disabledKeyColor: '#73777F',
    topBorderColor: 'rgba(179, 184, 193, 0.5)',
  },
  launchScreen: {
    bgColor: ['#009FD4', '#2669db'],
    brandTitleTextColor: '#fff',
    footerTextColor: 'rgba(255, 255, 255, 0.8)',
    footerCopyrightTextColor: 'rgba(255, 255, 255, 0.5)',
    footerPoweredByTextColor: 'rgba(255, 255, 255, 0.35)',
    activityIndicatorColor: 'rgba(255, 255, 255, 0.2)',
  },
  auth: {
    mainPanelBgColor: '#eee',
    sidePanelBgColor: '#009FD4',
    sidePanelVideoBgColor: '#000000',
    sideTitleTextColor: 'rgba(255, 255, 255, 1)',
    sideSubTitleTextColor: 'rgba(255, 255, 255, 0.7)',
    loginHeaderTitleTextColor: '#009FD4',
    loginHeaderSubTitleTextColor: 'rgba(0, 0, 0, 0.5)',
    linkTextColor: '#009FD4',
    buttonBgColor: '#009FD4',
    buttonTextColor: '#fff',
    disabledButtonBgColor: '#CED1D9',
    disabledButtonTextColor: 'rgba(0, 0, 0, 0.15)',
    agreementLabelTextColor: 'rgba(0, 0, 0, 0.5)',
    agreementCheckBoxFillColor: '#009FD4',
    backButtonColor: '#9498A2',
    avatarBgColor: '#fff',
    codeFieldBgColor: '#fff',
    codeFieldTextColor: '#009FD4',
    codeTipTextColor: '#4A4A4A',
    codeTtlTextColor: '#9498A2',
    textInput: {
      bgColor: '#fff',
      textColor: '#000',
      placeholderTextColor: 'rgba(0, 0, 0, 0.5)',
      tvFocusColor: '#ddd',
    },
    loginMethods: {
      titleTextColor: '#9498A2',
      separatorLineColor: '#CED1D9',
      iconBgColor: '#fff',
      iconColor: '#000000',
    },
  },
  report: {
    tab: {
      bgColor: '#e1e1e1',
      activeColor: StatusColors.Info,
      inactiveColor: Colors.Gray1,
    },
    construction: {
      section: {
        header: {
          titleColor: '#207FE7',
        },
      },
      procedure: {
        titleColor: '#333',
      },
    },
  },
  order: {
    iconColor: '#207FE7',
    labelColor: '#777',
    helpTextColor: '#aaa',
    placeholderColor: '#CCCCCC',
    valueColor: '#333333',
    accessoryViewColor: '#999999',
    tagTextColor: '#555',
    tagBorderColor: '#aaa',
    tagBgColor: '#f7f7f7',
  },
  inspection: {
    siteGrid: {
      cellBgColor: '#ffffff',
      headerBgColor: '#ffffff',
      borderColor: '#eee',
      headerTextColor: '#333333',
      headerButtonColor: '#fff',
      headerButtonBorderColor: '#eee',
      siteIconColor: '#444',
      defaultSiteNameColor: '#333333',
      tagColors: {
        'hardware-capable': '#207FE7',
        'with-pending-issue': '#DA9544',
        required: 'rgba(246, 121, 67, 1)',
        service: '#207FE7',
      },
    },
    siteInspection: {
      itemCard: {
        bgColor: '#fff',
        shadowColor: 'rgba(0, 0, 0, 0.1)',
        optionBorderColor: '#f2f2f2',
        titleIndexColor: '#000',
        titleColor: '#000',
        mediaPlaceholderBgColor: '#f1f1f1',
        mediaPlaceholderIconColor: '#999',
        unselectedOptionTextColor: '#555',
        unselectedOptionBgColor: '#fff',
        maintenanceAdviceLabelColor: '#333',
        maintenanceAdviceTextColor: '#207FE7',
        maintenanceAdviceBorderColor: '#207FE7',
        maintenanceAdviceBgColor: '#fff',
        relatedFaultLikeSubject: {
          borderColor: '#eee',
          textColor: '#777',
          activeTextColor: '#207FE7',
        },
      },
      valueInput: {
        bgColor: '#f5f5f5',
        textColor: '#000',
        placeholderTextColor: '#ccc',
        iconColor: '#999',
        errorBgColor: Color(StatusColors.Danger).alpha(0.1).string(),
        errorTextColor: StatusColors.Danger,
      },
    },
    customIssue: {
      labelColor: '#666',
      placeholderColor: '#aaa',
      textColor: '#111',
      helpTextColor: '#888',
      borderColor: '#e5e5e5',
    },
  },
  construction: {
    schedule: {
      tab: {
        primary: {
          activeTextColor: '#333',
          inactiveTextColor: '#989898',
        },
        secondary: {
          activeTextColor: '#565656',
          inactiveTextColor: '#565656',
          activeBgColor: '#E7E7E7',
          inactiveBgColor: 'transparent',
        },
      },
      search: {
        bgColor: '#F4F4F4',
        placeholderTextColor: '#999999',
        iconColor: '#999999',
        textColor: '#333',
      },
      workItem: {
        cell: {
          bgLight: '#fff',
          bgDark: '#F2F2F2',
          textLight: '#000',
          textDark: '#333',
        },
        listSection: {
          bgColor: '#f3f3f3',
          titleColor: '#2669db',
        },
      },
    },
  },
};

export default theme;
